import React, { useEffect, useRef, useState } from "react"
import {
  Ticket,
  ArrowRight,
  Smartphone,
  Lock,
  Loader2,
  CheckCircle2,
  MessageCircle,
} from "lucide-react"

import * as S from "./styled"
import { Api } from "../../api"
import getStore from "../../store"
import { useNavigate } from "react-router-dom"
import Dialog from "../../components/DIalog"
import { formatPhone } from "../../utils/masks/phone"
import {
  extractOtpDigits,
  fillOtpDigits,
  OTP_LENGTH,
  otpInputDigits,
} from "../../utils/tb/otp"

const phoneLength = 13 // 00 00000-0000
const RESEND_SECONDS = 60
const RESEND_KEY = "otpResendAt"

const getRemainingSeconds = () => {
  try {
    const target = Number(localStorage.getItem(RESEND_KEY))
    if (!target) return 0
    return Math.min(
      RESEND_SECONDS,
      Math.max(0, Math.ceil((target - Date.now()) / 1000)),
    )
  } catch {
    return 0
  }
}

const startResendCooldown = () => {
  try {
    localStorage.setItem(
      RESEND_KEY,
      String(Date.now() + RESEND_SECONDS * 1000),
    )
  } catch {}
}

export default function Login() {
  const [step, setStep] = useState<1 | 2>(1)
  const [phone, setPhone] = useState("")
  const [otp, setOtp] = useState(["", "", "", "", "", ""])
  const [isLoading, setIsLoading] = useState(false)
  const [, setFailedCODE] = useState(false)
  const [dialogData, setDialogData] = useState({
    opened: false,
    title: "",
    description: "",
  })

  const [resendIn, setResendIn] = useState(getRemainingSeconds)
  const [sentViaSms, setSentViaSms] = useState(false)

  const handleSentChannel = (data: object) => {
    setSentViaSms("channel" in data && data.channel === "sms")
  }

  const store = getStore()
  const navigate = useNavigate()

  useEffect(() => {
    const tick = () => setResendIn(getRemainingSeconds())
    const id = window.setInterval(tick, 1000)
    document.addEventListener("visibilitychange", tick)
    window.addEventListener("focus", tick)
    return () => {
      window.clearInterval(id)
      document.removeEventListener("visibilitychange", tick)
      window.removeEventListener("focus", tick)
    }
  }, [])

  const otpInputRefs = useRef<(HTMLInputElement | null)[]>([])

  useEffect(() => {
    store.controllers.user.clear()
    sessionStorage.removeItem("user")
    localStorage.removeItem("token")
    localStorage.removeItem("logged")
  }, [store.controllers.user])

  /* ---------- ETAPA 0: CHECK EVENT ---------- */

  // useEffect(() => {
  //   if (step === 2) {
  //     otpInputRefs.current[0]?.focus()
  //   }
  // }, [step])

  /* ---------- ETAPA 1: TELEFONE ---------- */

  const handleSendCode = async (e: React.FormEvent) => {
    e.preventDefault()
    if (phone.length < phoneLength) return

    setIsLoading(true)

    try {
      const cleanPhone = phone.replace(/\D/g, "")

      const dk = store.event?.dk ?? ""

      if (cleanPhone.length === 11) {
        await Api.post.login
          .requestCode({
            phone: cleanPhone,
            dk: dk,
          })
          .then((res) => {
            if (res.ok) {
              handleSentChannel(res.data)
              startResendCooldown()
              setResendIn(getRemainingSeconds())
              setStep(2)
            } else
              setDialogData({
                opened: true,
                title: "Erro ao enviar código",
                description: res.error,
              })

            setIsLoading(false)
          })
      } else alert("Digite um número válido")
    } catch (error) {}
  }

  /* ---------- ETAPA 2: OTP ---------- */

  useEffect(() => {
    if (step === 2) {
      otpInputRefs.current[0]?.focus()
    }
  }, [step])

  const handleResendCode = async (smsOnly = false) => {
    if (resendIn > 0 || isLoading) return

    const cleanPhone = phone.replace(/\D/g, "")
    if (cleanPhone.length !== 11) return

    startResendCooldown()
    setResendIn(getRemainingSeconds())

    const res = await Api.post.login.requestCode({
      phone: cleanPhone,
      smsOnly,
      dk: store.event?.dk ?? "",
    })

    if (res.ok) {
      handleSentChannel(res.data)
    } else {
      try {
        localStorage.removeItem(RESEND_KEY)
      } catch {}
      setResendIn(0)
      setDialogData({
        opened: true,
        title: "Erro ao reenviar código",
        description: res.error,
      })
    }
  }

  // Coloca os dígitos nos quadrados (código inteiro ou parte) e entra
  // quando os 6 estão preenchidos.
  const applyOtpDigits = (index: number, digits: string) => {
    const { otp: newOtp, focus } = fillOtpDigits(otp, index, digits)
    setOtp(newOtp)
    otpInputRefs.current[focus]?.focus()

    const combined = newOtp.join("")
    if (combined.length === OTP_LENGTH) handleLogin(combined)
  }

  const handleOtpChange = (index: number, value: string) => {
    const digits = otpInputDigits(otp[index], value)

    // Teclado do celular e preenchimento automático inserem o código
    // inteiro de uma vez, sem disparar o evento de colar.
    if (digits.length > 1) {
      applyOtpDigits(index, extractOtpDigits(digits))
      return
    }

    const newOtp = [...otp]
    newOtp[index] = digits
    setOtp(newOtp)

    if (digits && index < OTP_LENGTH - 1) {
      otpInputRefs.current[index + 1]?.focus()
    }

    const combined = newOtp.join("")
    if (digits && combined.length === OTP_LENGTH) {
      handleLogin(combined)
    }
  }

  const handleOtpPaste = (
    index: number,
    e: React.ClipboardEvent<HTMLInputElement>,
  ) => {
    const digits = extractOtpDigits(e.clipboardData?.getData("text") ?? "")
    // Sem texto legível no evento: deixa o navegador inserir e o onChange trata.
    if (!digits) return

    e.preventDefault()
    applyOtpDigits(index, digits)
  }

  const handleOtpKeyDown = (
    index: number,
    e: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (e.key === "Backspace" && !otp[index] && index > 0) {
      otpInputRefs.current[index - 1]?.focus()
    }
  }

  const handleLogin = async (code: string) => {
    setIsLoading(true)

    const dk = store.event?.dk ?? ""

    try {
      const login = await Api.post.login.validateCode({
        phone: phone.replace(/\D/g, ""),
        code,
        dk: dk,
      })

      if (login.ok) {
        store.controllers.user.setData(login.data)

        sessionStorage.setItem("user", JSON.stringify(login.data))
        navigate("/myTickets")
      } else {
        alert(
          "Código inválido. Verifique se o código e o telefone estão corretos e tente novamente.",
        )
        setFailedCODE(true)
      }
    } catch (error) {
      setFailedCODE(true)
    }

    setIsLoading(false)
  }

  const handleVerifyCode = (e: React.FormEvent) => {
    e.preventDefault()
    handleLogin(otp.join(""))
  }

  return (
    <S.Container>
      <Dialog
        open={dialogData.opened}
        variant="error"
        title={dialogData.title || "Erro ao processar código"}
        description={dialogData.description || "Tente novamente mais tarde."}
        onClose={() =>
          setDialogData({ opened: false, title: "", description: "" })
        }
      />
      <S.Wrapper>
        {/* ---------- HEADER ---------- */}
        <S.Header>
          <S.HeaderIcon>
            <Ticket size={32} color="#2563eb" />
          </S.HeaderIcon>

          <S.HeaderTitle>Fazer Login</S.HeaderTitle>
          <S.HeaderSubtitle>
            Entre com seu celular para acessar sua conta.
          </S.HeaderSubtitle>
        </S.Header>

        {/* ---------- CARD ---------- */}
        <S.Card>
          <S.ProgressBar>
            <S.Progress step={step} />
          </S.ProgressBar>

          {step === 1 ? (
            /* ---------- ETAPA 1 ---------- */
            <S.Form onSubmit={handleSendCode}>
              <S.Field>
                <S.Label>
                  <Smartphone size={16} color="#60a5fa" />
                  WhatsApp / Celular
                </S.Label>

                <S.InputWrapper>
                  <S.Input
                    type="tel"
                    autoComplete="tel"
                    placeholder="(00) 00000-0000"
                    value={phone}
                    onChange={({ target }) =>
                      setPhone(formatPhone(target.value).slice(0, 13))
                    }
                    required
                  />

                  {phone.length === 15 && (
                    <S.InputCheckIcon>
                      <CheckCircle2 size={20} />
                    </S.InputCheckIcon>
                  )}
                </S.InputWrapper>

                <S.Hint>
                  <MessageCircle size={14} color="#3b82f6" />
                  <span>
                    Enviaremos o código de acesso pelo <strong>WhatsApp</strong>
                    .
                  </span>
                </S.Hint>
              </S.Field>

              <S.PrimaryButton
                type="submit"
                disabled={phone.length < phoneLength || isLoading}
              >
                {isLoading ? (
                  <S.Loader>
                    <Loader2 />
                  </S.Loader>
                ) : (
                  <>
                    Receber Código
                    <ArrowRight size={20} />
                  </>
                )}
              </S.PrimaryButton>
            </S.Form>
          ) : (
            /* ---------- ETAPA 2 ---------- */
            <S.Form onSubmit={handleVerifyCode}>
              <S.Step2Header>
                <S.Step2Icon>
                  <MessageCircle size={24} />
                </S.Step2Icon>

                <S.Step2Title>
                  {sentViaSms ? "Verifique seu SMS" : "Verifique seu WhatsApp"}
                </S.Step2Title>
                <S.Step2Subtitle>
                  {sentViaSms
                    ? "Não conseguimos enviar pelo WhatsApp, então enviamos o código por SMS para"
                    : "Enviamos o código para"}{" "}
                  <strong style={{ color: "#0f172a" }}>{phone}</strong>
                </S.Step2Subtitle>

                <S.EditPhoneButton type="button" onClick={() => setStep(1)}>
                  Número errado? Corrigir
                </S.EditPhoneButton>
              </S.Step2Header>

              <S.Field>
                <S.Label style={{ justifyContent: "center" }}>
                  <Lock size={16} color="#60a5fa" />
                  Digite o código de 6 dígitos
                </S.Label>

                <S.OtpGroup>
                  {otp.map((digit, index) => (
                    <S.OtpInput
                      key={index}
                      ref={(el) => (otpInputRefs.current[index] = el)}
                      type="text"
                      inputMode="numeric"
                      autoComplete="one-time-code"
                      pattern="\d*"
                      value={digit}
                      onChange={(e) => handleOtpChange(index, e.target.value)}
                      onKeyDown={(e) => handleOtpKeyDown(index, e)}
                      onPaste={(e) => handleOtpPaste(index, e)}
                    />
                  ))}
                </S.OtpGroup>
              </S.Field>

              <S.PrimaryButton
                type="submit"
                disabled={otp.join("").length < 6 || isLoading}
              >
                {isLoading ? (
                  <>
                    <S.Loader>
                      <Loader2 />
                    </S.Loader>
                    Validando...
                  </>
                ) : (
                  <>
                    Entrar no Evento
                    <ArrowRight size={20} />
                  </>
                )}
              </S.PrimaryButton>

              <S.OtpFooter>
                <p>{sentViaSms ? "Não recebeu o SMS?" : "Não recebeu no WhatsApp?"}</p>
                <S.ResendButton
                  type="button"
                  disabled={resendIn > 0 || isLoading}
                  onClick={() => handleResendCode()}
                >
                  {resendIn > 0 ? `Reenviar em ${resendIn}s` : "Reenviar código"}
                </S.ResendButton>
                {/* Sempre visível: quem bloqueou o remetente do WhatsApp só
                    recebe o código por SMS. */}
                <S.ResendButton
                  type="button"
                  disabled={resendIn > 0 || isLoading}
                  onClick={() => handleResendCode(true)}
                >
                  {resendIn > 0 ? `Reenviar SMS em ${resendIn}s` : "Reenviar por SMS"}
                </S.ResendButton>
              </S.OtpFooter>
            </S.Form>
          )}
        </S.Card>
      </S.Wrapper>
    </S.Container>
  )
}
